<!--
  Componente presentacional del análisis CIEC/Syntage (ADR-0031).
  Consumido por customer-detail (mode='client') y lease-algorithm (mode='lease-snapshot').
  Sin estado HTTP — emite (refresh) y el padre dispara la mutación.
-->
<!-- Toast propio con `key`: avisa del fallo al descargar el PDF del 32-D (`SnapshotFiles`). -->
<p-toast position="top-right" [key]="toastKey"></p-toast>
<!-- El PDF de la foto se abre aquí; «Descargar» dentro del visor lo guarda con el nombre que manda el backend. -->
<app-file-preview-dialog
  [visible]="!!pdfs.preview"
  [fileBlob]="pdfs.preview?.blob ?? null"
  [fileName]="pdfs.preview?.fileName ?? null"
  [downloadable]="true"
  (visibleChange)="$event || pdfs.closePreview()"
  (downloadRequested)="pdfs.savePreview()"
></app-file-preview-dialog>
<!--
  El aviso de que hay un análisis de Ekatena para este RFC. Se define aquí una sola vez y se pinta
  en los DOS estados de la ficha.

  ! Vivía solo dentro del bloque «hay análisis», que es justo donde menos falta hace. El caso de
  ! mayor valor es el contrario —cliente sin análisis y un reporte esperando en Ekatena— y ahí no se
  ! veía nunca. Si se vuelve a mover, tiene que seguir alcanzando al estado vacío.
-->
<ng-template #avisoEkatena>
  <!--
    Mientras se pregunta a Ekatena. El padre ya llevaba la cuenta en `ekatenaBuscando` y la pasaba,
    pero aquí no se pintaba nada: la ficha se veía idéntica a «no hay nada que importar» hasta que
    la respuesta llegaba. El usuario leía el vacío como un hecho, no como una espera, y se iba
    antes de que apareciera el aviso.
  -->
  <div class="aviso-ekatena aviso-ekatena--buscando" *ngIf="ekatenaBuscando && !yaEsDeEkatena">
    <i class="fa-solid fa-circle-notch fa-spin"></i>
    <div class="aviso-ekatena__cuerpo">
      <strong>Buscando análisis previos para este RFC…</strong>
      <span>Si ya se corrió uno en Ekatena, aparece aquí y se puede traer sin gastar créditos.</span>
    </div>
  </div>

  <div
    class="aviso-ekatena"
    *ngIf="!ekatenaBuscando && hayEkatenaDisponible && !yaEsDeEkatena"
    [class.aviso-ekatena--valioso]="ekatenaAportaScoreReal"
  >
    <i class="fa-solid fa-file-import"></i>
    <div class="aviso-ekatena__cuerpo">
      <strong>Hay un análisis de Ekatena para este RFC.</strong>
      <span>
        Score {{ ekatenaReport?.score }}/1000 · {{ ekatenaReport?.availableReports?.length }} indicadores · actualizado
        {{ t.formatDate(ekatenaReport?.updatedAt) }}.
        <ng-container *ngIf="ekatenaAportaScoreReal && !isEmpty">
          <strong>Este análisis no tiene score real y ése sí.</strong>
        </ng-container>
      </span>
      <span class="aviso-ekatena__nota">
        Traerlo no consume créditos: el reporte ya está pagado.
        <ng-container *ngIf="!isEmpty">Se agrega como análisis aparte, sin reemplazar el actual.</ng-container>
      </span>
    </div>
    <button
      pButton
      type="button"
      class="p-button-sm p-button-outlined"
      icon="fa-solid fa-file-import"
      label="Importar"
      [loading]="ekatenaImportando"
      [disabled]="!canMutate || ekatenaImportando"
      (click)="importEkatena.emit()"
    ></button>
  </div>
</ng-template>

<div class="ciec-analysis">
  <!-- ─── ESTADOS ──────────────────────────────────────────────────────── -->

  <!-- Sin verificación: empty state canónico -->
  <ng-container *ngIf="isEmpty; else nonEmpty">
    <div class="empty-state">
      <i class="fa-solid fa-id-card-clip"></i>
      <h3>Sin análisis fiscal disponible</h3>
      <p>Ejecuta la verificación CIEC para extraer la información fiscal del SAT y calcular el rango de financiamiento.</p>
      <!-- Antes que los botones: si ya hay un análisis esperando, traerlo es más barato y más
           rápido que pedirle la CIEC al cliente otra vez. -->
      <div class="empty-ekatena"><ng-container *ngTemplateOutlet="avisoEkatena"></ng-container></div>

      <div class="empty-actions">
        <!-- ADR-0032 · Botón primario: solicitar al cliente por magic link.
             Es el flujo recomendado — el backend manda correo + el modal expone
             un link copiable para que el usuario interno lo comparta. -->
        <p-button
          *ngIf="canRequestCiec && canMutate"
          label="Solicitar CIEC al cliente"
          icon="fa-solid fa-envelope"
          [disabled]="refreshing"
          (onClick)="onRequestCiec()"
        >
        </p-button>
        <!-- ADR-0032 · Botón secundario: el usuario interno ingresa la CIEC a mano
             (cuando la tiene) → corre el análisis sin depender de que la meta el
             cliente. Abre un modal en el padre para capturar la contraseña CIEC. -->
        <!-- `mode === 'client'` en el *ngIf porque `onEnterCiec()` no hace nada fuera
             de esa vista: en el panel del lease el botón se renderizaba y al pulsarlo
             no pasaba nada. -->
        <p-button
          *ngIf="canMutate && mode === 'client'"
          label="Ingresar CIEC"
          icon="fa-solid fa-key"
          [outlined]="canRequestCiec"
          [disabled]="!canRefresh || refreshing"
          [loading]="refreshing"
          (onClick)="onEnterCiec()"
        >
        </p-button>
      </div>
      <small *ngIf="emptyStateNote" class="empty-reason">
        {{ emptyStateNote }}
      </small>
      <small *ngIf="!canRefresh && refreshDisabledReason" class="empty-reason">
        {{ refreshDisabledReason }}
      </small>
    </div>
  </ng-container>

  <ng-template #nonEmpty>
    <!-- Extracción en proceso -->
    <ng-container *ngIf="isPending; else notPending">
      <div class="state-card pending">
        <div class="state-spinner">
          <i class="fa-solid fa-arrows-rotate fa-spin"></i>
        </div>
        <h3>Procesando información fiscal</h3>
        <p>Estamos extrayendo los datos del SAT con Syntage. Este proceso puede tomar 1–2 minutos. Te avisaremos cuando esté listo.</p>
      </div>
    </ng-container>

    <ng-template #notPending>
      <!-- Verificación fallida -->
      <ng-container *ngIf="isFailed; else success">
        <div class="state-card failed">
          <div class="state-icon">
            <i class="fa-solid fa-circle-xmark"></i>
          </div>
          <h3>La verificación falló</h3>
          <p>{{ failureReason }}</p>
          <div class="state-actions">
            <p-button
              *ngIf="canRequestCiec && canMutate"
              label="Solicitar CIEC al cliente"
              icon="fa-solid fa-envelope"
              [disabled]="refreshing"
              (onClick)="onRequestCiec()"
            >
            </p-button>
            <p-button
              *ngIf="canMutate"
              label="Reintentar"
              icon="fa-solid fa-arrows-rotate"
              severity="danger"
              [outlined]="true"
              [disabled]="!canRefresh || refreshing"
              [loading]="refreshing"
              (onClick)="onRefresh()"
            >
            </p-button>
          </div>
          <!-- El motivo va como texto, no como tooltip: un botón deshabilitado no
               dispara eventos de puntero, así que `pTooltip` nunca se mostraría. -->
          <small *ngIf="!canRefresh && refreshDisabledReason" class="state-reason">
            {{ refreshDisabledReason }}
          </small>
        </div>
      </ng-container>

      <!-- ─── ÉXITO: vista completa ─────────────────────────────────────── -->
      <ng-template #success>
        <!-- 1. HERO -->
        <section class="section-header">
          <div class="header-content">
            <div class="header-left">
              <i class="fa-solid fa-shield-halved"></i>
              <div class="header-text">
                <h1>Análisis Fiscal &amp; CIEC</h1>
                <p class="header-subtitle">{{ heroSubtitle }}</p>
                <!-- Este análisis no salió del proveedor principal. Sin decirlo, una oferta de
                     respaldo es indistinguible de una normal, y no dicen lo mismo. -->
                <p class="aviso-respaldo" *ngIf="vieneDeRespaldo"><i class="fa-solid fa-arrows-turn-right"></i>{{ respaldoMotivo }}</p>
                <!-- B-075 · Análisis «exitoso» sin credencial del SAT: el monto no dice nada del cliente. -->
                <p class="aviso-sin-credencial" *ngIf="hasNoSatCredential" role="status" data-test="no-sat-credential">
                  <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ noSatCredentialNotice }}
                </p>

                <ng-container *ngTemplateOutlet="avisoEkatena"></ng-container>
              </div>
            </div>
            <div class="header-right">
              <p-tag [value]="statusLabel" [severity]="statusSeverity" [icon]="statusIcon" data-test="status-tag"> </p-tag>
              <p-tag *ngIf="syntageScore > 0" [value]="scoreTagLabel" severity="info" icon="pi pi-chart-bar"> </p-tag>
              <!-- ADR-0032 · "Solicitar CIEC al cliente" en el hero solo cuando el estado
                   amerita pedirle una nueva al cliente (expirada/fallida). Si la verificación
                   está vigente, este botón se oculta y "Actualizar análisis" reusa la CIEC
                   almacenada en bóveda automáticamente. -->
              <button
                *ngIf="canRequestCiec && canMutate"
                pButton
                type="button"
                label="Solicitar CIEC al cliente"
                icon="fa-solid fa-envelope"
                [disabled]="refreshing"
                class="p-button-sm p-button-outlined"
                (click)="onRequestCiec()"
              ></button>
              <button
                *ngIf="canMutate"
                pButton
                type="button"
                [label]="refreshing ? 'Actualizando…' : 'Actualizar análisis'"
                icon="fa-solid fa-arrows-rotate"
                [disabled]="!canRefresh || refreshing"
                [loading]="refreshing"
                [pTooltip]="!canRefresh ? refreshDisabledReason : ''"
                tooltipPosition="bottom"
                class="p-button-sm"
                (click)="onRefresh()"
              ></button>
            </div>
          </div>
          <!-- El `pTooltip` de arriba no alcanza: el navegador suprime los eventos de
               puntero sobre un botón deshabilitado, así que el motivo nunca aparecía.
               Se renderiza también como texto visible. -->
          <small *ngIf="!canRefresh && refreshDisabledReason" class="header-reason">
            <i class="fa-solid fa-circle-info"></i>
            {{ refreshDisabledReason }}
          </small>
        </section>

        <!-- 2. Franja de KPIs: capacidad, score y decisión, con el lenguaje de las sub-pestañas (F-158). -->
        <div class="alg-kpis summary-kpis" data-test="summary-kpis">
          <!-- 2.a Capacidad estimada -->
          <div class="alg-kpi" data-test="capacity-kpi">
            <span class="alg-kpi__label">Capacidad estimada</span>
            <!-- El monto a la izquierda y el mínimo a la derecha; debajo, la barra a todo el ancho de la casilla. -->
            <span class="summary-row">
              <span class="alg-kpi__value summary-capacity" [class.is-ok]="maxOfferAmount > 0" [class.is-bad]="maxOfferAmount === 0"
                >{{ t.formatCurrencyCompact(maxOfferAmount) }}<span class="alg-kpi__unit"> MXN máx</span></span
              >
              <span class="alg-kpi__sub summary-min" *ngIf="minOfferAmount > 0" data-test="capacity-min"
                ><i class="fa-solid fa-shield-halved" aria-hidden="true"></i><span class="p-hidden-accessible">Oferta conservadora: </span
                ><span aria-hidden="true">mín. </span><strong>{{ t.formatCurrencyCompact(minOfferAmount) }}</strong></span
              >
            </span>
            <div class="summary-bar" *ngIf="maxOfferAmount > 0">
              <div class="summary-bar__track">
                <div class="summary-bar__fill is-capacity" [style.width.%]="100"></div>
                <div class="summary-bar__marker" [style.left.%]="capacityProgress"></div>
              </div>
            </div>
          </div>

          <!-- 2.b Score del proveedor que produjo este análisis -->
          <div class="alg-kpi" data-test="provider-score" [class.is-assumed]="scoreEsSupuesto">
            <span class="alg-kpi__label summary-label"
              >Score {{ providerLabel }}
              <!-- El marcador que faltaba: un score supuesto y uno real se veían idénticos. -->
              <span
                class="chip-supuesto"
                *ngIf="scoreEsSupuesto"
                pTooltip="El proveedor no devolvió score. Se usó el valor supuesto de la configuración, así que el grado y la tasa de esta oferta no están respaldados por un score real."
                tooltipPosition="top"
                >supuesto</span
              ></span
            >

            <!-- Caso con score: valor, calificación del algoritmo y barra en la escala del proveedor -->
            <ng-container *ngIf="hasSyntageScore; else noScore">
              <span class="summary-row">
                <!-- La escala del proveedor, no la nuestra: enseñar el convertido sería inventarle un número.
                     La banda es la del proveedor sobre su escala de 1000 (F-158). -->
                <span class="alg-kpi__value summary-score"
                  >{{ syntageScore
                  }}<span class="alg-kpi__unit">
                    / {{ scoreEscalaLabel }}<span class="stat-band" *ngIf="scoreBand"> · {{ scoreBand }}</span></span
                  ></span
                >
                <!-- La calificación A-D es del ALGORITMO, no del proveedor: va rotulada para no leerse como la banda. -->
                <span
                  class="summary-grade"
                  data-test="algorithm-grade"
                  [pTooltip]="gradeTooltip"
                  tooltipPosition="top"
                  tabindex="0"
                  aria-describedby="algorithm-grade-help"
                  ><span>Calificación</span><strong class="grade-chip" [ngClass]="'is-' + gradeTone">{{ algorithmGrade }}</strong
                  ><span id="algorithm-grade-help" class="p-hidden-accessible">{{ gradeTooltip }}</span></span
                >
              </span>
              <!-- F-158: sin los marcadores 650/750 del algoritmo. Sobre 1000, el fondo marca las cuatro bandas de Syntage. -->
              <div class="summary-bar">
                <div class="summary-bar__track" [class.score-bands]="scoreBand">
                  <div class="summary-bar__fill is-score" [style.width.%]="scoreProgress"></div>
                </div>
              </div>
            </ng-container>

            <!-- Caso sin score: mensaje claro, sin chip rojo engañoso -->
            <ng-template #noScore>
              <div class="stat-no-data" data-test="no-score">
                <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                <div class="no-data-body">
                  <strong>Sin score crediticio</strong>
                  <span>{{ providerLabel }} no devolvió score para este RFC. Evalúa con el resto del análisis fiscal.</span>
                </div>
              </div>
            </ng-template>
          </div>

          <!-- 2.c Estado / Decisión -->
          <div
            class="alg-kpi"
            data-test="decision-kpi"
            *ngIf="mode === 'client'"
            [class.is-approved]="clientStateApproved"
            [class.is-rejected]="isExpired"
          >
            <span class="alg-kpi__label">Estado del análisis</span>
            <span class="summary-decision"
              ><i class="fa-solid" [ngClass]="clientStateIcon" aria-hidden="true"></i><span>{{ clientStateLabel }}</span></span
            >
            <span class="alg-kpi__sub" *ngIf="clientStateHint" data-test="client-state-hint">{{ clientStateHint }}</span>
          </div>

          <div
            class="alg-kpi"
            data-test="decision-kpi"
            *ngIf="mode === 'lease-snapshot'"
            [class.is-approved]="decisionApproved"
            [class.is-rejected]="!decisionApproved && verification?.decision === 'rejected_no_range'"
          >
            <span class="alg-kpi__label">Decisión del arrendamiento</span>
            <ng-container *ngIf="verification?.decision; else noDecision">
              <span class="summary-decision"
                ><i class="fa-solid" [ngClass]="decisionApproved ? 'fa-circle-check' : 'fa-circle-xmark'" aria-hidden="true"></i
                ><span>{{ decisionLabel }}</span></span
              >
            </ng-container>
            <!-- Sin decisión todavía: lo dice en vez de un «—» suelto. La vista no recibe el estado del arrendamiento. -->
            <ng-template #noDecision>
              <span class="summary-decision is-pending" data-test="no-decision"
                ><i class="fa-solid fa-circle-minus" aria-hidden="true"></i><span>Sin decisión todavía</span></span
              >
              <span class="alg-kpi__sub">Se define en el Comité</span>
            </ng-template>
            <span class="alg-kpi__sub" *ngIf="verification?.requiredDownPayment > 0">
              Enganche: {{ t.formatCurrency(verification.requiredDownPayment) }}
            </span>
            <span class="alg-kpi__sub" *ngIf="verification?.creditAmount > 0 && !verification?.requiredDownPayment">
              Monto a financiar: {{ t.formatCurrency(verification.creditAmount) }}
            </span>
          </div>
        </div>

        <!-- Ofertas calculadas: dos tarjetas compactas justo debajo de la franja, el segundo bloque de atención. -->
        <section class="offers-section" *ngIf="result?.minOffer || result?.maxOffer">
          <div class="offers-grid">
            <!-- Oferta conservadora -->
            <article class="offer-card is-min" *ngIf="result?.minOffer" data-test="offer-min">
              <div class="offer-card__body">
                <header class="offer-card__head">
                  <span class="offer-card__icon"><i class="fa-solid fa-shield-halved" aria-hidden="true"></i></span>
                  <span class="offer-card__label">Oferta conservadora</span>
                </header>
                <div class="offer-card__amount">{{ t.formatCurrency(conservativeOffer?.amount ?? 0) }}</div>
                <div class="offer-card__sub">MXN máximo financiable</div>
                <div class="offer-strip">
                  <div class="offer-stat">
                    <div class="offer-stat__label">Tasa anual</div>
                    <div class="offer-stat__value">{{ result.minOffer.interestRate }}%</div>
                  </div>
                  <div class="offer-stat">
                    <div class="offer-stat__label">Meses</div>
                    <div class="offer-stat__value">{{ result.minOffer.termMonths }}</div>
                  </div>
                  <div class="offer-stat">
                    <div class="offer-stat__label offer-strip__help">
                      Pago mensual
                      <i
                        class="stat-help-icon fa-solid fa-circle-info"
                        pTooltip="Pago mensual es una estimación referencial; la tabla de amortización oficial se genera al crear el lease."
                        tabindex="0"
                        role="img"
                        aria-label="Pago mensual es una estimación referencial; la tabla de amortización oficial se genera al crear el lease."
                        tooltipPosition="top"
                        tooltipStyleClass="ciec-help-tooltip"
                      >
                      </i>
                    </div>
                    <div class="offer-stat__value">{{ minOfferMonthly !== null ? t.formatCurrencyCompact(minOfferMonthly) : '—' }}</div>
                  </div>
                </div>
              </div>
            </article>

            <!-- Oferta máxima -->
            <article class="offer-card is-max" *ngIf="result?.maxOffer" data-test="offer-max">
              <div class="offer-card__body">
                <header class="offer-card__head">
                  <span class="offer-card__icon"><i class="fa-solid fa-rocket" aria-hidden="true"></i></span>
                  <span class="offer-card__label">Oferta máxima</span>
                </header>
                <div class="offer-card__amount">{{ t.formatCurrency(result.maxOffer.amount) }}</div>
                <div class="offer-card__sub is-delta" *ngIf="maxOfferDelta as d; else maxSubDefault">
                  <i class="fa-solid fa-arrow-trend-up" aria-hidden="true"></i>
                  <strong>+{{ t.formatCurrencyCompact(d.amount) }} · +{{ d.percent.toFixed(0) }}%</strong>
                  <span class="vs">vs conservadora</span>
                </div>
                <ng-template #maxSubDefault>
                  <div class="offer-card__sub">MXN máximo financiable</div>
                </ng-template>
                <div class="offer-strip">
                  <div class="offer-stat">
                    <div class="offer-stat__label">Tasa anual</div>
                    <div class="offer-stat__value">{{ result.maxOffer.interestRate }}%</div>
                  </div>
                  <div class="offer-stat">
                    <div class="offer-stat__label">Meses</div>
                    <div class="offer-stat__value">{{ result.maxOffer.termMonths }}</div>
                  </div>
                  <div class="offer-stat is-highlight">
                    <div class="offer-stat__label offer-strip__help">
                      Pago mensual
                      <i
                        class="stat-help-icon fa-solid fa-circle-info"
                        pTooltip="Pago mensual es una estimación referencial; la tabla de amortización oficial se genera al crear el lease."
                        tabindex="0"
                        role="img"
                        aria-label="Pago mensual es una estimación referencial; la tabla de amortización oficial se genera al crear el lease."
                        tooltipPosition="top"
                        tooltipStyleClass="ciec-help-tooltip"
                      >
                      </i>
                    </div>
                    <div class="offer-stat__value">{{ maxOfferMonthly !== null ? t.formatCurrencyCompact(maxOfferMonthly) : '—' }}</div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <!--
          F-158 · Sub-pestañas. Arriba queda lo de siempre (hero, score, capacidad y ofertas); debajo,
          Análisis con el detalle de hoy y, solo para quien tiene `leases.view`, Buró y deuda y Legal y
          corporativo. Sin íconos y con subrayado navy (`algoritmo-tabs`, acotado a este componente). Cada panel va en `pTemplate="content"`: se instancia —y pide su vista— al
          abrirse por primera vez (`docs/design/lease-detail-tabs.md` § Nota sobre carga de datos).
        -->
        <p-tabView styleClass="tabview-custom algoritmo-tabs" [(activeIndex)]="subtab">
          <p-tabPanel>
            <ng-template pTemplate="header">
              <span>Análisis</span>
            </ng-template>
            <ng-template pTemplate="content">
              <article class="alg-card analysis-panel" aria-label="Análisis">
                <!-- ─── Encabezado: proveedor, RFC, verificación y revisión de la foto ─── -->
                <header class="alg-card__header">
                  <span class="alg-card__icon"><i class="fa-solid fa-chart-line" aria-hidden="true"></i></span>
                  <div class="alg-card__titles">
                    <h2>Perfil fiscal y riesgos</h2>
                    <!-- La fecha del reporte del proveedor va en el `title`: la tarjeta «Identidad fiscal» ya no existe. -->
                    <p
                      data-test="analysis-subtitle"
                      [attr.title]="'Reporte ' + providerLabel + ': ' + t.formatDateTime(verification?.syntageReportedAt)"
                    >
                      {{ providerLabel }} · RFC <span class="alg-mono">{{ verification?.rfc || '—' }}</span> · verificado el
                      <strong>{{ shortDate(verification?.verifiedAt) }}</strong>
                    </p>
                  </div>
                  <span class="alg-pill is-neutral" *ngIf="photo?.snapshot?.viewedRevision as revision" data-test="snapshot-revision"
                    >Foto · revisión {{ revision }}</span
                  >
                </header>

                <!-- F-158 · Si la foto no carga se pinta lo guardado en el análisis, pero se dice: sin el
                     aviso, «No consultado» en las listas se leería como un hecho y no como un hueco. -->
                <div class="alg-notice is-bad" *ngIf="photoLoadFailed" role="alert" data-test="photo-load-error">
                  <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                  <span>No pudimos cargar la foto de Syntage; abajo se muestra lo que se guardó en el análisis.</span>
                  <button type="button" class="alg-btn alg-btn--xs" data-test="photo-retry" (click)="retryPhoto()">
                    <i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i>Reintentar
                  </button>
                </div>
                <!-- Foto armada después del análisis: lo que se ve de Syntage es de ese día, no del análisis. -->
                <app-snapshot-state
                  *ngIf="photo?.snapshot?.reconstructed"
                  state="reconstructed"
                  [builtAt]="photo?.snapshot?.reconstructedAt"
                  [analyzedAt]="photo?.snapshot?.originalVerifiedAt ?? verification?.verifiedAt"
                ></app-snapshot-state>

                <div class="alg-card__body">
                  <!-- ─── Franja de 5: facturación, margen, empleados, 32-D y listas ─── -->
                  <div class="alg-kpis analysis-kpis">
                    <div class="alg-kpi" data-test="billing-kpi">
                      <span class="alg-kpi__label"
                        >Facturación {{ latestFinance?.year ?? ''
                        }}<ng-container *ngIf="billingThrough"> (a {{ billingThrough }})</ng-container></span
                      >
                      <span
                        class="alg-kpi__value"
                        [attr.title]="latestFinance?.billed !== null ? pesos(latestFinance?.billed ?? 0) : null"
                        >{{ compact(latestFinance?.billed) }}</span
                      >
                      <span class="alg-kpi__sub" *ngIf="latestFinance?.delta !== null && latestFinance?.delta !== undefined; else noDelta"
                        ><strong [class.is-bad]="latestFinance!.delta! < 0">{{ delta(latestFinance!.delta) }}</strong> vs
                        {{ latestFinance!.year - 1 }}<ng-container *ngIf="previousYearComplete"> completo</ng-container></span
                      >
                      <ng-template #noDelta><span class="alg-kpi__sub">sin año anterior para comparar</span></ng-template>
                    </div>
                    <div class="alg-kpi" data-test="margin-kpi">
                      <span class="alg-kpi__label">Margen {{ latestFinance?.year ?? '' }}</span>
                      <span class="alg-kpi__value" [class.is-bad]="(latestFinance?.margin ?? 0) < 0">{{
                        percent(latestFinance?.margin ?? null)
                      }}</span>
                      <span
                        class="alg-kpi__sub"
                        *ngIf="latestFinance?.income !== null && latestFinance?.income !== undefined; else noComparison"
                        >ingresos {{ compact(latestFinance?.income) }} · gastos {{ compact(latestFinance?.expenses) }}</span
                      >
                      <ng-template #noComparison><span class="alg-kpi__sub">sin comparativo anual</span></ng-template>
                    </div>
                    <div class="alg-kpi" data-test="employees-kpi">
                      <span class="alg-kpi__label">Empleados</span>
                      <span class="employees-row">
                        <span class="alg-kpi__value" data-test="employees-current">{{ employeesCurrent ?? '—' }}</span>
                        <span
                          class="employees-spark"
                          *ngIf="employeesSpark.length"
                          role="img"
                          data-test="employees-spark"
                          [attr.aria-label]="employeesSparkLabel"
                        >
                          <span
                            *ngFor="let bar of employeesSpark; let last = last; trackBy: trackByIdx"
                            class="employees-spark__bar"
                            [class.is-alert]="last && employeesCurrent === 0"
                            [style.height.px]="bar.height"
                            [attr.title]="bar.title"
                          ></span>
                        </span>
                      </span>
                      <span class="alg-kpi__sub" *ngIf="employeesCurrent === 0">0 puede ser falta de nómina del mes anterior</span>
                    </div>
                    <div class="alg-kpi tax-kpi" data-test="tax-compliance-kpi">
                      <!-- PDF de la opinión tal como la emitió el SAT, en la esquina de la casilla: mismo estado de copia que Buró y BIL. -->
                      <app-snapshot-pdf-button
                        *ngIf="pdfs.offers('tax_compliance')"
                        class="tax-kpi__pdf"
                        data-test="pdf-tax-compliance"
                        subject="la opinión de cumplimiento (32-D)"
                        [status]="pdfs.status('tax_compliance')"
                        [opening]="pdfs.downloading === 'tax_compliance'"
                        (open)="pdfs.open('tax_compliance')"
                      ></app-snapshot-pdf-button>
                      <span class="alg-kpi__label">Cumplimiento fiscal (32-D)</span>
                      <span class="alg-kpi__value" [class.is-bad]="taxCompliance?.risky" data-test="tax-compliance-value">{{
                        photoLoading ? 'Consultando…' : (taxCompliance?.value ?? '—')
                      }}</span>
                      <span class="tax-row">
                        <span class="alg-pill is-bad" *ngIf="!photoLoading && taxCompliance?.risky">En riesgo</span>
                        <!-- La nota del PDF (copiándose, falla pasajera, ya no está) se queda aquí, visible: un tooltip en un botón deshabilitado no sale. -->
                        <ng-container *ngIf="pdfs.status('tax_compliance')">
                          <span class="alg-file-note" *ngIf="pdfs.note('tax_compliance') as note" data-test="pdf-tax-compliance-note">{{
                            note
                          }}</span>
                        </ng-container>
                      </span>
                    </div>
                    <!-- F-158 · Nunca «Limpio»: «Sin hallazgos» solo cuando las tres listas dicen qué consultaron; mientras llega
                         la foto, «Consultando…»: lo guardado en el análisis cambiaría al llegar. -->
                    <div class="alg-kpi" data-test="lists-kpi">
                      <span class="alg-kpi__label">Listas restringidas</span>
                      <span class="alg-kpi__value" data-test="lists-verdict" [ngClass]="'is-' + listsVerdict.tone">{{
                        listsVerdict.label
                      }}</span>
                      <span class="alg-kpi__sub" *ngIf="!photoLoading; else listsLoading" data-test="lists">
                        <ng-container *ngFor="let b of listBadges; let last = last; trackBy: trackByIdx">
                          <span
                            data-test="list-badge"
                            [attr.data-list]="b.label"
                            [attr.data-state]="b.state"
                            [attr.title]="b.text + (b.source ? ' · ' + b.source : '')"
                            [class.is-bad]="b.state === 'found' || b.state === 'error'"
                            >{{ b.label }} {{ listShort(b)
                            }}<span class="p-hidden-accessible">: {{ b.text }}{{ b.source ? ', ' + b.source : '' }}</span></span
                          ><ng-container *ngIf="!last"> · </ng-container>
                        </ng-container>
                        <ng-container *ngIf="listsInBases"> bases</ng-container>
                      </span>
                      <ng-template #listsLoading>
                        <span class="alg-kpi__sub" aria-busy="true" data-test="lists-loading">
                          <ng-container *ngFor="let label of ['PEP', 'Sanciones', 'OFAC']; let last = last; trackBy: trackByIdx">
                            <span data-test="list-badge" [attr.data-list]="label" data-state="loading"
                              >{{ label }}<span class="p-hidden-accessible">: Consultando…</span></span
                            ><ng-container *ngIf="!last"> · </ng-container>
                          </ng-container>
                        </span>
                      </ng-template>
                    </div>
                  </div>

                  <!-- ─── Riesgos según Syntage: una línea por indicador, los marcados primero ─── -->
                  <div class="alg-block" data-test="risks">
                    <div class="alg-sec">
                      <h3>Riesgos según Syntage</h3>
                      <ng-container *ngIf="!photoLoading && riskItems.length">
                        <span class="alg-pill" [ngClass]="riskyCount ? 'is-bad' : 'is-ok'" data-test="risky-count"
                          >{{ riskyCount }} en riesgo</span
                        >
                        <span class="risks-note">de {{ riskItems.length }} indicadores · en riesgo primero</span>
                      </ng-container>
                    </div>
                    <app-snapshot-state *ngIf="photoLoading" state="loading" data-test="risks-loading"></app-snapshot-state>
                    <ng-container *ngIf="!photoLoading">
                      <app-snapshot-state
                        *ngIf="risksState"
                        [state]="risksState"
                        [closesAt]="photo?.snapshot?.windowEndsAt"
                        [reason]="photo?.sections?.risks?.reason"
                      ></app-snapshot-state>
                      <div class="risk-grid" *ngIf="riskItems.length">
                        <div
                          class="risk"
                          *ngFor="let r of sortedRiskItems; trackBy: trackByIdx"
                          data-test="risk-item"
                          [class.is-bad]="r.risky"
                        >
                          <span class="risk__k" data-test="risk-label">{{ r.label }}</span>
                          <span class="risk__v" data-test="risk-value">{{ r.value }}</span>
                          <span class="p-hidden-accessible" *ngIf="r.risky">, en riesgo</span>
                        </div>
                      </div>
                    </ng-container>
                  </div>

                  <!-- Finanzas: una tabla por año y las dos gráficas lado a lado -->
                  <section
                    class="alg-block analysis-section"
                    *ngIf="hasSection('finance')"
                    data-test="section-finance"
                    aria-labelledby="analysis-section-finance"
                  >
                    <div class="alg-sec"><h3 id="analysis-section-finance">Finanzas</h3></div>
                    <div class="alg-card__stack">
                      <div class="alg-table-wrap" *ngIf="financeRows.length">
                        <table class="alg-table finance-table">
                          <caption class="p-hidden-accessible">
                            Facturación y comparativo anual
                          </caption>
                          <colgroup>
                            <col style="width: 110px" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th scope="col">Año</th>
                              <th scope="col" class="is-num">Facturado</th>
                              <th scope="col" class="is-num">Δ vs año ant.</th>
                              <th scope="col" class="is-num">Ingresos</th>
                              <th scope="col" class="is-num">Gastos</th>
                              <th scope="col" class="is-num">Margen</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr *ngFor="let row of financeRows; let first = first; trackBy: trackByIdx" data-test="billing-row">
                              <td class="is-num-plain">{{ row.label }}</td>
                              <td class="is-num">{{ row.billed !== null ? pesos(row.billed) : '—' }}</td>
                              <td class="is-num" [class.is-neg]="(row.delta ?? 0) < 0" [class.is-strong]="first" data-test="billing-delta">
                                {{ delta(row.delta) }}
                              </td>
                              <td class="is-num">{{ row.income !== null ? pesos(row.income) : '—' }}</td>
                              <td class="is-num">{{ row.expenses !== null ? pesos(row.expenses) : '—' }}</td>
                              <td class="is-num" [ngClass]="(row.margin ?? 0) < 0 ? 'is-neg' : row.margin !== null ? 'is-pos' : ''">
                                {{ percent(row.margin) }}
                              </td>
                            </tr>
                            <tr class="is-total" *ngIf="financeTotal as total" data-test="billing-total">
                              <td>{{ total.label }}</td>
                              <td class="is-num">{{ total.billed !== null ? pesos(total.billed) : '—' }}</td>
                              <td class="is-num"></td>
                              <td class="is-num">{{ total.income !== null ? pesos(total.income) : '—' }}</td>
                              <td class="is-num">{{ total.expenses !== null ? pesos(total.expenses) : '—' }}</td>
                              <td class="is-num" [ngClass]="(total.margin ?? 0) < 0 ? 'is-neg' : total.margin !== null ? 'is-pos' : ''">
                                {{ percent(total.margin) }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="alg-grid" *ngIf="cashBars.length || salesBars.length">
                        <div class="alg-box chart-box" *ngIf="cashBars.length" data-test="cash-flow">
                          <div class="alg-sec">
                            <h3>Cash flow mensual</h3>
                            <span class="chart-legend"
                              ><span><i class="legend-dot is-in" aria-hidden="true"></i>Ingresos</span
                              ><span><i class="legend-dot is-out" aria-hidden="true"></i>Egresos</span></span
                            >
                          </div>
                          <div
                            class="chart-bars"
                            role="img"
                            [attr.aria-label]="'Cash flow mensual de ' + cashAxis?.from + ' a ' + cashAxis?.to"
                          >
                            <span class="chart-pair" *ngFor="let bar of cashBars; trackBy: trackByIdx">
                              <span class="chart-bar is-in" [style.height.px]="bar.inflow.height" [attr.title]="bar.inflow.title"></span>
                              <span class="chart-bar is-out" [style.height.px]="bar.outflow.height" [attr.title]="bar.outflow.title"></span>
                            </span>
                          </div>
                          <div class="chart-axis" aria-hidden="true">
                            <span>{{ cashAxis?.from }}</span
                            ><span>{{ cashAxis?.to }}</span>
                          </div>
                        </div>
                        <div class="alg-box chart-box" *ngIf="salesBars.length" data-test="sales">
                          <div class="alg-sec">
                            <h3>Ingresos por ventas</h3>
                            <span class="chart-legend">{{ salesBars.length }} meses</span>
                          </div>
                          <div
                            class="chart-bars is-dense"
                            role="img"
                            [attr.aria-label]="'Ingresos por ventas de ' + salesAxis?.from + ' a ' + salesAxis?.to"
                          >
                            <span
                              class="chart-bar is-sales"
                              *ngFor="let bar of salesBars; trackBy: trackByIdx"
                              [style.height.px]="bar.height"
                              [attr.title]="bar.title"
                            ></span>
                          </div>
                          <div class="chart-axis" aria-hidden="true">
                            <span>{{ salesAxis?.from }}</span
                            ><span>{{ salesAxis?.to }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Concentración y 69-B -->
                  <section
                    class="alg-block analysis-section"
                    *ngIf="hasSection('concentration')"
                    data-test="section-concentration"
                    aria-labelledby="analysis-section-concentration"
                  >
                    <div class="alg-sec"><h3 id="analysis-section-concentration">Concentración y 69-B</h3></div>
                    <div class="alg-card__stack">
                      <div class="alg-grid concentration-grid" *ngIf="customerConcentration.length || supplierConcentration.length">
                        <div class="alg-box conc-box" *ngIf="customerConcentration.length" data-test="top-customers">
                          <div class="alg-sec">
                            <h3>Top clientes</h3>
                            <span
                              class="conc-hhi"
                              *ngIf="concentrationRisk('customerConcentration') as risk"
                              [class.is-bad]="risk.risky"
                              data-test="customers-hhi"
                              >{{ risk.value }}<ng-container *ngIf="risk.risky"> · en riesgo</ng-container></span
                            >
                          </div>
                          <div
                            class="conc-row"
                            *ngFor="let c of topConcentration(customerConcentration); trackBy: trackByIdx"
                            data-test="customer-row"
                          >
                            <span class="conc-name" [attr.title]="c?.rfc ? (c?.name || c?.razonSocial) + ' · ' + c.rfc : null">{{
                              c?.name || c?.razonSocial || '—'
                            }}</span>
                            <span class="conc-track" aria-hidden="true"
                              ><span class="conc-fill is-customer" [style.width.%]="c?.share || 0"></span
                            ></span>
                            <span class="conc-share">{{ (c?.share || 0).toFixed(1) }} %</span>
                            <span class="conc-total">{{ compact(c?.total) }}</span>
                          </div>
                        </div>
                        <div class="alg-box conc-box" *ngIf="supplierConcentration.length" data-test="top-suppliers">
                          <div class="alg-sec">
                            <h3>Top proveedores</h3>
                            <span
                              class="conc-hhi"
                              *ngIf="concentrationRisk('supplierConcentration') as risk"
                              [class.is-bad]="risk.risky"
                              data-test="suppliers-hhi"
                              >{{ risk.value }}<ng-container *ngIf="risk.risky"> · en riesgo</ng-container></span
                            >
                          </div>
                          <div
                            class="conc-row"
                            *ngFor="let c of topConcentration(supplierConcentration); trackBy: trackByIdx"
                            data-test="supplier-row"
                          >
                            <span class="conc-name" [attr.title]="c?.rfc ? (c?.name || c?.razonSocial) + ' · ' + c.rfc : null">{{
                              c?.name || c?.razonSocial || '—'
                            }}</span>
                            <span class="conc-track" aria-hidden="true"
                              ><span class="conc-fill is-supplier" [style.width.%]="c?.share || 0"></span
                            ></span>
                            <span class="conc-share">{{ (c?.share || 0).toFixed(1) }} %</span>
                            <span class="conc-total">{{ compact(c?.total) }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="alg-block" data-test="blacklist">
                        <div class="alg-sec">
                          <h3 class="is-small">Contrapartes en lista 69-B</h3>
                          <span class="alg-pill is-neutral" *ngIf="!photoLoading && blacklistRows.length">{{ blacklistRows.length }}</span>
                          <!-- La ventana importa: el detalle es histórico y el indicador de riesgo cuenta solo 12 meses, así que los
                               dos números pueden no coincidir sin que ninguno esté mal. -->
                          <span class="risks-note" *ngIf="!photoLoading && blacklistWindowLabel" data-test="blacklist-window"
                            >{{ blacklistWindowLabel }}: todas las facturas con contrapartes que hoy están en la lista 69-B; el indicador de
                            riesgos cuenta solo los últimos 12 meses</span
                          >
                        </div>
                        <app-snapshot-state *ngIf="photoLoading" state="loading" data-test="blacklist-loading"></app-snapshot-state>
                        <ng-container *ngIf="!photoLoading">
                          <app-snapshot-state
                            *ngIf="blacklistState"
                            [state]="blacklistState"
                            [closesAt]="photo?.snapshot?.windowEndsAt"
                            [reason]="photo?.sections?.blacklistedCounterparties?.reason"
                          ></app-snapshot-state>
                          <div class="alg-table-wrap" *ngIf="blacklistRows.length">
                            <table class="alg-table blacklist-table">
                              <caption class="p-hidden-accessible">
                                Contrapartes del cliente que aparecen en la lista 69-B del SAT
                              </caption>
                              <colgroup>
                                <col />
                                <col style="width: 150px" />
                                <col style="width: 120px" />
                                <col style="width: 90px" />
                                <col style="width: 150px" />
                              </colgroup>
                              <thead>
                                <tr>
                                  <th scope="col">Contraparte</th>
                                  <th scope="col">RFC</th>
                                  <th scope="col">Estatus 69-B</th>
                                  <th scope="col" class="is-num">Facturas</th>
                                  <th scope="col">Emitida / recibida</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr *ngFor="let row of blacklistRows; trackBy: trackByIdx" data-test="blacklist-row">
                                  <td>{{ row.name }}</td>
                                  <td class="is-mono blacklist-rfc">{{ row.rfc }}</td>
                                  <td>
                                    <span class="alg-pill" [ngClass]="'is-' + blacklistTone(row.status)">{{ row.status }}</span>
                                  </td>
                                  <td class="is-num">{{ row.invoices }}</td>
                                  <td>{{ row.direction }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </ng-container>
                      </div>
                    </div>
                  </section>

                  <!-- Cálculo de oferta: capacidad base, factores y el compuesto -->
                  <section
                    class="alg-block analysis-section"
                    *ngIf="hasSection('offer')"
                    data-test="section-offer"
                    aria-labelledby="analysis-section-offer"
                  >
                    <div class="alg-sec"><h3 id="analysis-section-offer">Cálculo de oferta</h3></div>
                    <div class="alg-card__stack">
                      <div class="alg-strip capacity-strip" *ngIf="capacityRationale as cap" data-test="capacity">
                        <div class="alg-strip__cell">
                          <div class="alg-strip__title">{{ cap.title }}</div>
                          <div class="alg-strip__hint">el máximo de {{ cap.inputs?.length ?? 0 }} fuentes</div>
                          <div class="alg-strip__value is-ok">{{ cap.output.value }}</div>
                        </div>
                        <div
                          class="alg-strip__cell"
                          *ngFor="let src of cap.inputs; let i = index; trackBy: trackByIdx"
                          [class.is-chosen]="i === chosenCapacitySource"
                          data-test="capacity-source"
                        >
                          <div class="alg-kpi__label">{{ src.label }}</div>
                          <div class="alg-strip__value">{{ src.value }}</div>
                          <div class="capacity-chosen" *ngIf="i === chosenCapacitySource">la que se eligió</div>
                        </div>
                      </div>
                      <div class="alg-table-wrap" *ngIf="factorRows.length || compositeRationale">
                        <table class="alg-table factors-table">
                          <caption class="p-hidden-accessible">
                            Factores del cálculo de oferta
                          </caption>
                          <colgroup>
                            <col class="is-fill-14" />
                            <col class="is-fill-16" />
                            <col style="width: 120px" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th scope="col">Factor</th>
                              <th scope="col">Entrada</th>
                              <th scope="col" class="is-num">Multiplicador</th>
                            </tr>
                          </thead>
                          <tbody>
                            <ng-container *ngFor="let r of factorRows; trackBy: trackByIdx">
                              <tr data-test="factor-row" [class.is-open]="r.kind === 'score' && scoreDetailOpen">
                                <td>
                                  <span class="factor-cell">
                                    <!-- Los aspectos positivos y negativos del score (antes la tarjeta «Scores crediticios»). -->
                                    <button
                                      *ngIf="r.kind === 'score' && scoreBreakdowns.length"
                                      type="button"
                                      class="alg-exp"
                                      data-test="score-toggle"
                                      [attr.aria-expanded]="scoreDetailOpen"
                                      aria-label="Ver los aspectos positivos y negativos del score"
                                      (click)="scoreDetailOpen = !scoreDetailOpen"
                                    >
                                      <i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button
                                    >{{ r.title }}
                                  </span>
                                </td>
                                <td class="is-secondary">{{ r.inputValue?.label }} {{ r.inputValue?.value }}</td>
                                <td class="is-num is-strong">× {{ r.output.value }}</td>
                              </tr>
                              <tr class="alg-detail" *ngIf="r.kind === 'score' && scoreDetailOpen" data-test="score-detail">
                                <td colspan="3">
                                  <div class="score-breakdown" *ngFor="let breakdown of scoreBreakdowns; trackBy: trackByIdx">
                                    <span class="alg-lbl">{{ breakdown.source }}</span>
                                    <div class="alg-detail__grid score-breakdown__cols">
                                      <div *ngIf="breakdown.positives.length">
                                        <div class="breakdown-title is-ok">Aspectos positivos</div>
                                        <div class="breakdown-item" *ngFor="let item of breakdown.positives; trackBy: trackByIdx">
                                          <strong>{{ t.translateMetricKey(item?.key) }}</strong> ·
                                          {{ t.translateMetricValue(item?.value || item?.description) }}
                                        </div>
                                      </div>
                                      <div *ngIf="breakdown.negatives.length">
                                        <div class="breakdown-title is-bad">Aspectos negativos</div>
                                        <div class="breakdown-item" *ngFor="let item of breakdown.negatives; trackBy: trackByIdx">
                                          <strong>{{ t.translateMetricKey(item?.key) }}</strong> ·
                                          {{ t.translateMetricValue(item?.value || item?.description) }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </ng-container>
                            <tr class="is-total" *ngIf="compositeRationale as composite" data-test="composite-row">
                              <td class="is-heavy">{{ composite.title }}</td>
                              <td>
                                {{ composite.output.label }}: <span class="composite-amount">{{ composite.output.value }}</span>
                              </td>
                              <td class="is-num is-heavy">{{ composite.inputValue?.value }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p class="raw-rationale" *ngFor="let r of rawRationales; trackBy: trackByIdx" data-test="raw-rationale">
                        <i class="fa-solid fa-circle-info" aria-hidden="true"></i>{{ t.translateRationale(r.raw) }}
                      </p>
                    </div>
                  </section>

                  <!-- Decisión del arrendamiento (solo en el arrendamiento): no está en la maqueta y no se puede perder -->
                  <section
                    class="alg-block analysis-section"
                    *ngIf="hasSection('decision')"
                    data-test="section-decision"
                    aria-labelledby="analysis-section-decision"
                  >
                    <div class="alg-sec"><h3 id="analysis-section-decision">Decisión del arrendamiento</h3></div>
                    <div class="alg-card__stack" data-test="decision">
                      <div class="alg-strip decision-strip">
                        <div class="alg-strip__cell">
                          <div class="alg-strip__title">Decisión</div>
                          <span class="alg-pill" [ngClass]="'is-' + decisionTone">{{ decisionLabel }}</span>
                        </div>
                        <div class="alg-strip__cell">
                          <div class="alg-kpi__label">Monto solicitado</div>
                          <div class="alg-strip__value">{{ t.formatCurrency(verification?.requestedAmount) }}</div>
                        </div>
                        <div class="alg-strip__cell">
                          <div class="alg-kpi__label">Rango aprobado</div>
                          <div class="alg-strip__value">
                            {{ t.formatCurrencyCompact(verification?.rangeMin) }} – {{ t.formatCurrencyCompact(verification?.rangeMax) }}
                          </div>
                        </div>
                        <div class="alg-strip__cell" *ngIf="verification?.requiredDownPayment > 0">
                          <div class="alg-kpi__label">Diferencia a cubrir (enganche)</div>
                          <div class="alg-strip__value">{{ t.formatCurrency(verification.requiredDownPayment) }}</div>
                        </div>
                        <div class="alg-strip__cell" *ngIf="verification?.creditAmount > 0">
                          <div class="alg-kpi__label">Monto a financiar</div>
                          <div class="alg-strip__value is-ok">{{ t.formatCurrency(verification.creditAmount) }}</div>
                        </div>
                      </div>
                      <ul class="reasons-list" *ngIf="verification?.generalReasons?.length">
                        <li *ngFor="let r of verification.generalReasons; trackBy: trackByIdx">{{ r }}</li>
                      </ul>
                      <span class="alg-pill is-bad decision-rejection" *ngIf="verification?.rejectionReason">{{
                        t.translateRejectionReason(verification.rejectionReason)
                      }}</span>
                    </div>
                  </section>
                </div>
              </article>
            </ng-template>
          </p-tabPanel>

          <p-tabPanel *ngIf="canViewSnapshotTabs && snapshotContext">
            <ng-template pTemplate="header">
              <span>Buró y deuda</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-bureau-tab [context]="snapshotContext" [analyzedAt]="verification?.verifiedAt"></app-ciec-bureau-tab>
            </ng-template>
          </p-tabPanel>

          <p-tabPanel *ngIf="canViewSnapshotTabs && snapshotContext">
            <ng-template pTemplate="header">
              <span>Legal y corporativo</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-legal-tab [context]="snapshotContext" [analyzedAt]="verification?.verifiedAt"></app-ciec-legal-tab>
            </ng-template>
          </p-tabPanel>

          <!-- Calculadora de riesgo (D-41): las pestañas del Excel de «Análisis de riesgo» en su orden. Las cuatro leen una sola
               respuesta (`RiskScorecardStore`), que se pide al abrir la primera. -->
          <p-tabPanel *ngIf="canViewRiskTabs">
            <ng-template pTemplate="header">
              <span>Flujos</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-flows-tab [context]="snapshotContext"></app-ciec-flows-tab>
            </ng-template>
          </p-tabPanel>

          <p-tabPanel *ngIf="canViewRiskTabs">
            <ng-template pTemplate="header">
              <span>Estados financieros</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-statements-tab [context]="snapshotContext"></app-ciec-statements-tab>
            </ng-template>
          </p-tabPanel>

          <p-tabPanel *ngIf="canViewRiskTabs">
            <ng-template pTemplate="header">
              <span>Ratios</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-ratios-tab [context]="snapshotContext" [fallbackSyntageRatios]="financialRatios"></app-ciec-ratios-tab>
            </ng-template>
          </p-tabPanel>

          <p-tabPanel *ngIf="canViewRiskTabs">
            <ng-template pTemplate="header">
              <span>Calculadora</span>
            </ng-template>
            <ng-template pTemplate="content">
              <app-ciec-risk-tab [context]="snapshotContext" (openTab)="openRiskTab($event)"></app-ciec-risk-tab>
            </ng-template>
          </p-tabPanel>
        </p-tabView>
        <!-- El motivo de no poder volver a correr el análisis va una sola vez, en el encabezado junto al botón
             (`.header-reason`); repetirlo al pie de las sub-pestañas lo duplicaba (F-158). -->
      </ng-template>
    </ng-template>
  </ng-template>
</div>
